/**
 * PdfDocument - Fluent builder for pdfmake documents
 *
 * This class provides a convenient API for building PDF documents using pdfmake.
 * It wraps the pdfmake API with a fluent interface for easier use.
 */

import type {
    TDocumentDefinitions,
    Content,
    StyleDictionary,
    TFontDictionary,
} from 'pdfmake/interfaces';
import type {
    PdfDocumentOptions,
    PdfGenerationResult,
    PdfTemplate,
    InvoiceTemplateData,
} from './types';
import { getPdfMake } from './pdfmakeLoader';

/**
 * PdfDocument class for building PDF documents
 *
 * @example
 * ```ts
 * const pdf = new PdfDocument({ pageSize: 'LETTER' })
 *     .addContent({ text: 'Hello World', style: 'header' })
 *     .addPageBreak()
 *     .addContent({ text: 'Page 2' });
 *
 * const { dataUrl, filename } = await pdf.generate('document.pdf');
 * ```
 */
export class PdfDocument {
    private options: PdfDocumentOptions;
    private content: Content[] = [];
    private styles: StyleDictionary = {};
    private images: Record<string, string> = {};

    constructor(options: PdfDocumentOptions = {}) {
        this.options = {
            pageSize: 'LETTER',
            pageMargins: 36, // 0.5 inch
            defaultFont: 'Roboto',
            defaultFontSize: 10,
            ...options,
        };
    }

    /**
     * Add an image to the document's image dictionary
     * @param name - Unique name to reference the image
     * @param dataUrl - Base64 data URL of the image
     */
    addImage(name: string, dataUrl: string): this {
        this.images[name] = dataUrl;
        return this;
    }

    /**
     * Set custom fonts for the document (global setting)
     */
    static setFonts(fonts: TFontDictionary): void {
        void getPdfMake()
            .then(pdfMake => { pdfMake.fonts = { ...pdfMake.fonts, ...fonts }; })
            .catch(err => console.error('[PdfDocument] setFonts failed:', err));
    }

    /**
     * Add styles to the document
     */
    addStyles(styles: StyleDictionary): this {
        this.styles = { ...this.styles, ...styles };
        return this;
    }

    /**
     * Add content to the document
     */
    addContent(...items: Content[]): this {
        this.content.push(...items);
        return this;
    }

    /**
     * Add a page break
     */
    addPageBreak(): this {
        this.content.push({ text: '', pageBreak: 'before' });
        return this;
    }

    /**
     * Add a horizontal rule/separator
     */
    addSeparator(color = '#e0e0e0', margin: [number, number, number, number] = [0, 10, 0, 10]): this {
        this.content.push({
            canvas: [
                {
                    type: 'line',
                    x1: 0,
                    y1: 0,
                    x2: 515, // Approximate page width minus margins
                    y2: 0,
                    lineWidth: 1,
                    lineColor: color,
                },
            ],
            margin,
        });
        return this;
    }

    /**
     * Add vertical spacing
     */
    addSpace(points = 10): this {
        this.content.push({ text: '', margin: [0, points, 0, 0] });
        return this;
    }

    /**
     * Build the pdfmake document definition
     */
    private buildDocumentDefinition(): TDocumentDefinitions {
        const def: TDocumentDefinitions = {
            pageSize: this.options.pageSize,
            pageMargins: this.options.pageMargins,
            info: {
                title: this.options.title,
                subject: this.options.subject,
                author: this.options.author,
                keywords: this.options.keywords,
            },
            defaultStyle: {
                font: this.options.defaultFont,
                fontSize: this.options.defaultFontSize,
            },
            styles: this.styles,
            content: this.content,

            // Orphan control: if a section header (headlineLevel 1) lands near
            // the bottom of a page with fewer than 3 content nodes following it,
            // push the header to the next page so it stays with its content.
            pageBreakBefore: (currentNode, { getFollowingNodesOnPage }) => {
                if (currentNode.headlineLevel === 1) {
                    const following = getFollowingNodesOnPage();
                    return following.length <= 3;
                }
                return false;
            },
        };

        // Add images dictionary if any images were registered
        if (Object.keys(this.images).length > 0) {
            def.images = this.images;
        }

        return def;
    }

    /**
     * Generate the PDF and return as a data URL
     */
    async toDataUrl(): Promise<string> {
        const pdfMake = await getPdfMake();
        const pdfDocGenerator = pdfMake.createPdf(this.buildDocumentDefinition());
        return pdfDocGenerator.getDataUrl();
    }

    /**
     * Generate the PDF and return as a Blob
     */
    async toBlob(): Promise<Blob> {
        const pdfMake = await getPdfMake();
        const pdfDocGenerator = pdfMake.createPdf(this.buildDocumentDefinition());
        return pdfDocGenerator.getBlob();
    }

    /**
     * Generate the PDF and trigger a download
     */
    download(filename: string): void {
        const docDefinition = this.buildDocumentDefinition();
        void getPdfMake()
            .then(pdfMake => pdfMake.createPdf(docDefinition).download(filename))
            .catch(err => console.error('[PdfDocument] download failed:', err));
    }

    /**
     * Generate the PDF and open in a new window
     */
    open(): void {
        const docDefinition = this.buildDocumentDefinition();
        void getPdfMake()
            .then(pdfMake => pdfMake.createPdf(docDefinition).open())
            .catch(err => console.error('[PdfDocument] open failed:', err));
    }

    /**
     * Generate the PDF with all results
     */
    async generate(filename: string): Promise<PdfGenerationResult> {
        const pdfMake = await getPdfMake();
        const pdfDoc = pdfMake.createPdf(this.buildDocumentDefinition());
        const dataUrl = await pdfDoc.getDataUrl();
        const blob = await pdfDoc.getBlob();
        return { dataUrl, filename, blob };
    }

    /**
     * Create a document from a template
     */
    static fromTemplate<T extends InvoiceTemplateData>(
        template: PdfTemplate<T>,
        data: T,
        optionOverrides?: Partial<PdfDocumentOptions>
    ): PdfDocument {
        const options = { ...template.defaultOptions, ...optionOverrides };
        const doc = new PdfDocument(options);

        doc.addStyles(template.styles);
        doc.addContent(...template.render(data));

        return doc;
    }
}

export default PdfDocument;
